import type { IncidentListItem } from '@prerender/diagnostics-api';
import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { accountNameLabel, planLabel } from './accountLabels';
import { formatDetectedAt } from './formatDetectedAt';
import { ABSENT_VALUE } from './formatMeasuredValue';
import IncidentStatusTag from './IncidentStatusTag';
import IncidentTypeCell from './IncidentTypeCell';
import { crawlerLabel } from './crawlerLabels';
import { domainSummary } from './domainSummary';
import { impactLines } from './impactLines';
import { incidentHeadline } from './incidentHeadline';
import { isNewTabClick, isPlainLeftClick } from './mouseClicks';
import { relativeTimeSince } from './relativeTimeSince';
import { incidentPath, OPENED_FROM_TRIAGE } from './routes';
import SeverityTag from './SeverityTag';
import { triggerValueLabel } from './triggerValueLabel';
import TwoLineCell from './TwoLineCell';

type IncidentRowProps = {
  incident: IncidentListItem;
  now: Date;
};

const dateLines = (iso: string, now: Date): { primary: string; secondary: string } => {
  const date = new Date(iso);

  return { primary: formatDetectedAt(date), secondary: `(${relativeTimeSince(date, now)})` };
};

const domainsLine = (incident: IncidentListItem): string => {
  const { domains, stillAffected } = domainSummary(incident);

  return stillAffected === null ? domains : `${domains} · ${stillAffected}`;
};

const incidentSecondLine = (incident: IncidentListItem): string =>
  incident.crawlers.length === 0
    ? domainsLine(incident)
    : `${domainsLine(incident)} · ${incident.crawlers.map(crawlerLabel).join(', ')}`;

const landedOnALink = (event: MouseEvent<HTMLTableRowElement>): boolean =>
  (event.target as HTMLElement).closest('a') !== null;

const endedATextSelection = (): boolean => (window.getSelection()?.toString() ?? '') !== '';

function IncidentRow({ incident, now }: IncidentRowProps) {
  const navigate = useNavigate();
  const incidentLink = { to: incidentPath(incident.reference), state: OPENED_FROM_TRIAGE };
  const openIncident = (event: MouseEvent<HTMLTableRowElement>): void => {
    if (landedOnALink(event) || endedATextSelection()) {
      return;
    }
    if (isNewTabClick(event)) {
      window.open(incidentLink.to, '_blank', 'noopener');
      return;
    }
    if (isPlainLeftClick(event)) {
      navigate(incidentLink.to, { state: incidentLink.state });
    }
  };

  return (
    <tr className="diagnostics-row" onClick={openIncident} onAuxClick={openIncident}>
      <td className="diagnostics-cell-reference">{incident.reference}</td>
      <td>
        <TwoLineCell primary={accountNameLabel(incident.account)} secondary={`User ID ${incident.userId}`} />
      </td>
      <td className="diagnostics-cell-compact">{planLabel(incident.account)}</td>
      <td>
        <SeverityTag severity={incident.severity} />
      </td>
      <td>
        <IncidentStatusTag status={incident.status} />
      </td>
      <td className="diagnostics-cell-compact">
        <TwoLineCell {...dateLines(incident.firstDetectedAt, now)} />
      </td>
      <td className="diagnostics-cell-compact">
        {incident.lastCheckedAt === null ? ABSENT_VALUE : <TwoLineCell {...dateLines(incident.lastCheckedAt, now)} />}
      </td>
      <td className="diagnostics-cell-compact">
        <IncidentTypeCell incidentType={incident.incidentType} crawlerCategory={incident.crawlerCategory} />
      </td>
      <td className="diagnostics-cell-incident">
        <TwoLineCell primary={incidentHeadline(incident)} secondary={incidentSecondLine(incident)} />
      </td>
      <td className="diagnostics-cell-compact">
        <TwoLineCell {...impactLines(incident)} />
      </td>
      <td className="diagnostics-cell-compact">{triggerValueLabel(incident)}</td>
      <td className="diagnostics-cell-review">
        <Link
          {...incidentLink}
          className="diagnostics-review-link"
          aria-label={`Review incident ${incident.headlineDomain}`}
        >
          <span aria-hidden="true">&rarr;</span>
        </Link>
      </td>
    </tr>
  );
}

export default IncidentRow;
